import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import formatWithThousandSeparator from '../../../helpers/FormatWithThousandSeparator';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const parseDateAtUtcMidnight = (value) => {
  if (!value) {
    return null;
  }

  const text = String(value).trim();
  let year;
  let month;
  let day;

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const frenchMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);

  if (isoMatch) {
    [, year, month, day] = isoMatch;
  } else if (frenchMatch) {
    [, day, month, year] = frenchMatch;
  } else {
    return null;
  }

  const timestamp = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== Number(year) ||
    parsed.getUTCMonth() !== Number(month) - 1 ||
    parsed.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  return timestamp;
};

const getInclusiveDayCount = (destinations) => {
  const starts = destinations
    .map((destination) => parseDateAtUtcMidnight(destination?.date_begin))
    .filter((value) => value !== null);
  const ends = destinations
    .map((destination) => parseDateAtUtcMidnight(destination?.date_end))
    .filter((value) => value !== null);

  if (!starts.length || !ends.length) {
    return null;
  }

  const firstDay = Math.min(...starts);
  const lastDay = Math.max(...ends);
  if (lastDay < firstDay) {
    return null;
  }

  return Math.floor((lastDay - firstDay) / DAY_IN_MS) + 1;
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const amount = Number(String(value).replace(/\s+/g, '').replace(',', '.'));
  return Number.isFinite(amount) ? amount : null;
};

export const getRentalChargeDetails = (destinations = [], transport = null) => {
  const rows = Array.isArray(destinations) ? destinations : [];
  const transportIsRental = transport?.mission_rent_id || transport?.rent_type;

  if (!transportIsRental) {
    return null;
  }

  const rental = transport || {};
  const paymentMode = String(rental.rent_paiement || '').trim().toLowerCase();
  const isDaily = paymentMode === 'daily';
  const isFlat = paymentMode === 'flat';
  const dayCount = getInclusiveDayCount(rows);
  const unitPrice = parseAmount(rental.rent_amount);
  const total =
    unitPrice === null || (isDaily && dayCount === null)
      ? null
      : isDaily
      ? unitPrice * dayCount
      : unitPrice;

  return {
    designation: rental.rent_type || '-',
    paymentLabel: isFlat
      ? 'Forfaitaire'
      : isDaily
      ? 'Paiement par jour'
      : rental.rent_paiement || '-',
    unitPrice,
    dayCount,
    total,
  };
};

const RentalChargeTable = ({ destinations = [], transport = null }) => {
  const rentalCharge = getRentalChargeDetails(destinations, transport);

  if (!rentalCharge) {
    return null;
  }

  const headers = [
    'Designation',
    'Mode de paiement',
    'Prix Unitaire',
    'Nombre de jour',
    'Montant',
  ];

  return (
    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
      <TableContainer
        sx={{
          width: '100%',
          overflowX: 'auto',
          border: '1px solid #c9d3df',
          borderRadius: '4px',
        }}
      >
        <Table size="small" aria-label="Frais de location" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  align="center"
                  sx={{
                    bgcolor: '#2f75b5',
                    color: '#fff',
                    fontWeight: 700,
                    borderRight: '1px solid #1f4e79',
                    whiteSpace: 'nowrap',
                    '&:last-child': { borderRight: 0 },
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell align="center">{rentalCharge.designation}</TableCell>
              <TableCell align="center">{rentalCharge.paymentLabel}</TableCell>
              <TableCell align="center">
                {formatWithThousandSeparator(rentalCharge.unitPrice, { fallback: '-' })}
              </TableCell>
              <TableCell align="center">{rentalCharge.dayCount ?? '-'}</TableCell>
              <TableCell align="center">
                {formatWithThousandSeparator(rentalCharge.total, { fallback: '-' })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RentalChargeTable;
